import { useState, useEffect, useRef } from "react";
import { ChromePicker } from 'react-color';
import { getPlans, addCompany, updateCompany, getCompany } from "../../../../api/General/Company";
import {
  SubscriptionPlan,
  CompanyInput,
  CountryView,
  OrganizationView,
  CompanyView,
  TimeZoneInfoView,
  Roles,
} from "../../../../models/general";
import { getCountries, getTimeZones } from "../../../../api/General/Country";
import { useTranslation } from "react-i18next";
import { useFileUploadLimits } from "../../../../hooks/useFileUploadLimits";
import { getRoles } from "../../../../api/General/account";
import { lightOrDark } from "../../../../utils";
import PhoneInput from 'react-phone-number-input';
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { GetOrganizations } from "../../../../api/General/organization";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import AppIcon from "../../../../components/AppIcon";
import { Spinner } from "../../../../components/ui/Spinner";
import OperatorMfaPolicyPanel from "../OperatorMfaPolicyPanel";
import SessionRevocationPanel from "../SessionRevocationPanel";

export default function AddEditCompany() {
  const { t } = useTranslation();
  const role = useSelector((s: any) => s.user.role);
  const params = useParams();
  const navigate = useNavigate();
  const { validateFileSize } = useFileUploadLimits();
  const ref = useRef<HTMLInputElement>(null);
  const id = Number(params.id);

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [countries, setCountries] = useState<CountryView[]>([]);
  const [timeZones, setTimeZones] = useState<TimeZoneInfoView[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationView[]>([]);
  const [input, setInput] = useState<CompanyInput>({
    organizationId: 0,
    countryId: 0,
    websiteLink: "",
    address: "",
    email: "",
    rolesMaxUsers: {},
    phoneNumber: "",
    image: null,
    name: "",
    planId: 0,
    primaryColor: "#304EA1",
    timeZone: "",
    locales: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
  });
  const [company, setCompany] = useState<CompanyView>({} as CompanyView);
  const [roles, setRoles] = useState<string[]>([]);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    if (id) {
      const companyRes = await getCompany(id);
      setInput({
        ...companyRes.payload,
        image: null,
        organizationId: companyRes.payload.organization?.id,
        planId: companyRes.payload.subscriptionPlanView?.id,
        countryId: companyRes.payload.country?.id,
        adminEmail: "",
        adminFirstName: "",
        adminLastName: "",
      });
      setCompany(companyRes.payload);
    }

    const [plansRes, countriesRes, timeZonesRes, rolesRes, orgsRes] = await Promise.all([
      getPlans(),
      getCountries(),
      getTimeZones(),
      getRoles(),
      GetOrganizations(),
    ]);

    setPlans(plansRes.payload);
    setCountries(countriesRes.payload);
    setTimeZones(timeZonesRes.payload);
    setRoles(rolesRes.payload || []);
    setOrganizations(orgsRes.payload);
    setLoading(false);
  };

  const handleImageChange = (event: React.FormEvent<HTMLInputElement>) => {
    const f = (event.target as any).files[0];
    if (f) {
      const { valid, maxMB } = validateFileSize(f, 'companyLogoMaxSizeMB');
      if (!valid) {
        alert(t('fileSizeExceedsLimit', { name: f.name, maxMB }));
        (event.target as any).value = '';
        return;
      }
    }
    setInput((prev) => ({ ...prev, image: f }));
  };

  const addNewCompany = async () => {
    try {
      setLoading(true);
      input.rolesMaxUsers = input.rolesMaxUsers || {};
      const result = await addCompany(input);
      setInput({
        organizationId: 0, countryId: 0, websiteLink: "", address: "", email: "",
        phoneNumber: "", image: null, rolesMaxUsers: {}, name: "", planId: 1,
        primaryColor: "#304EA1", timeZone: "", locales: "",
        adminFirstName: "", adminLastName: "", adminEmail: "",
      });
      setLoading(false);
      return result.payload.id;
    } catch {
      setLoading(false);
    }
    return undefined;
  };

  const updateOldCompany = async () => {
    if (id) {
      try {
        setLoading(true);
        await updateCompany(id, input);
        setSuccess(true);
        setLoading(false);
      } catch {
        setLoading(false);
      }
    }
  };

  const validateInputs = () => {
    return !(input.countryId == null || input.countryId === 0 || input.name == null || input.name === "");
  };

  const addOrUpdate = async () => {
    if (validateInputs()) {
      if (id) {
        await updateOldCompany();
      } else {
        roles.map((r) => (input.rolesMaxUsers[r] = input.rolesMaxUsers[r] || 0));
        await addNewCompany();
        navigate("/settings/companies");
      }
    }
  };

  const { rolesMaxUsers } = input;
  const { rolesUsersAvailable } = company;

  const countryOptions = countries.map(c => ({ value: c.id, label: c.name }));
  const timeZoneOptions = timeZones.map(tz => ({ value: tz.id, label: tz.displayName }));
  const orgOptions = organizations.map(o => ({ value: o.id, label: o.name }));

  return (
    <div className="flex-1">
      <div className="flex flex-col gap-5 p-4 items-center">
        {success && (
          <div role="alert" className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-700 dark:text-emerald-400">
            {t("Company Updated Successfully")}
          </div>
        )}
        <div className="flex flex-col gap-4 w-full max-w-lg">
          {role === Roles.Operator && (
            <Input
              required
              label={t("Company Name")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, name: e.target.value }))}
              value={input.name}
            />
          )}

          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <Button onClick={() => ref.current?.click()}>{t("Upload Image")}</Button>
              {input.image && (
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm text-foreground">{input.image.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setInput(prev => ({ ...prev, image: null }))}
                  >
                    <AppIcon name="Trash2" size={16} />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <input ref={ref} onChange={handleImageChange} type="file" className="hidden" accept="image/*" />

          <Input
            label={t("Email")}
            type="text"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, email: e.target.value }))}
            value={input.email}
          />

          {role === Roles.Operator && (
            <Select
              label={t("Organization")}
              required
              options={orgOptions}
              value={input.organizationId}
              onChange={(val: any) => setInput(prev => ({ ...prev, organizationId: val || 0 }))}
              searchable
            />
          )}

          <Select
            label={t("Country")}
            required
            options={countryOptions}
            value={input.countryId}
            onChange={(val: any) => setInput(prev => ({ ...prev, countryId: val || 0 }))}
            searchable
          />

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">{t('Phone Number')}</label>
            <PhoneInput
              international
              dir="ltr"
              className="h-[30px]"
              value={input.phoneNumber}
              onChange={(ev: any) => setInput(prev => ({ ...prev, phoneNumber: ev }))}
            />
          </div>

          <Select
            label={t("TimeZone")}
            required
            options={timeZoneOptions}
            value={input.timeZone}
            onChange={(val: any) => setInput(prev => ({ ...prev, timeZone: val || '' }))}
            searchable
          />

          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-foreground">
              {t("Licensed Users")} <span className="text-destructive">*</span>
            </label>
            <div className="rounded border border-border p-1">
              <div className="grid grid-cols-2 gap-4" dir="ltr">
                {roles.map((r) => (
                  <div key={r} className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground mb-1">{r}</span>
                    <div className="flex items-end">
                      {id ? (
                        <span className="text-sm text-foreground mb-1">
                          {`${rolesUsersAvailable?.[r] ?? 0}  /`}
                        </span>
                      ) : null}
                      {role === Roles.Operator ? (
                        <input
                          type="number"
                          min="0"
                          placeholder={t(r) + " " + t("Users")}
                          className="m-0.5 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          onChange={(ev) => {
                            const value = parseInt(ev.target.value || '0');
                            setInput(prev => ({
                              ...prev,
                              rolesMaxUsers: { ...prev.rolesMaxUsers, [r]: value },
                            }));
                          }}
                          value={rolesMaxUsers[r] as any}
                        />
                      ) : (
                        <span className="text-sm text-foreground mb-1">{rolesMaxUsers[r]}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {role === Roles.Operator && (
            <Input
              label={t("Locales")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, locales: e.target.value }))}
              value={input.locales}
            />
          )}

          {!id && role === Roles.Operator && (
            <>
              <Input
                required
                label={t("Admin First Name")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, adminFirstName: e.target.value }))}
                value={input.adminFirstName}
              />
              <Input
                required
                label={t("Admin Last Name")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, adminLastName: e.target.value }))}
                value={input.adminLastName}
              />
              <Input
                required
                label={t("Admin Email")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(prev => ({ ...prev, adminEmail: e.target.value }))}
                value={input.adminEmail}
              />
            </>
          )}

          <h2 className="text-sm font-semibold text-foreground">{t("Primary Color")}</h2>

          <ChromePicker
            color={input.primaryColor}
            onChangeComplete={(color) => {
              if (input.primaryColor !== color.hex && lightOrDark(color.hex) === 'light') {
                setAlertVisible(true);
                setAlertContent(t("Please choose a darker color"));
              } else {
                setAlertVisible(false);
                setInput(prev => ({ ...prev, primaryColor: color.hex }));
              }
            }}
          />

          {alertVisible && (
            <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
              {alertContent}
            </div>
          )}

          <Button
            fullWidth
            disabled={loading}
            onClick={addOrUpdate}
          >
            {loading ? <Spinner size="sm" /> : t("Submit")}
          </Button>

          {/* CO-323 — per-company MFA policy. Operator only (the API is Policy = "Operator"); saved on its own. */}
          {!!id && role === Roles.Operator && <OperatorMfaPolicyPanel companyId={id} />}

          {/* CO-708 — Operator danger zone: end every session in this company. Uses the SAVED name
              (company.name), not the editable input, because that is what the server confirms against. */}
          {id && role === Roles.Operator && company?.name ? (
            <SessionRevocationPanel scope="company" companyId={id} companyName={company.name} />
          ) : null}
        </div>
      </div>
    </div>
  );
}
